// COPIED from Remote-Leverage/hriq@3b50a234afdd packages/design-system/components/ui/v2/dropdown.tsx by apps/web/scripts/sync-design-system.mjs. Do not edit: change it in Hriq and re-sync.
// @ts-nocheck -- type-checked in Hriq under its own compiler options (the CRM's are stricter).
"use client";

import {
  DropdownMenuCheckboxItem as DropdownMenuCheckboxItemV1,
  DropdownMenuContent as DropdownMenuContentV1,
  DropdownMenuItem as DropdownMenuItemV1,
  DropdownMenuRadioItem as DropdownMenuRadioItemV1,
  DropdownMenuSeparator as DropdownMenuSeparatorV1,
  DropdownMenuSubContent as DropdownMenuSubContentV1,
  DropdownMenuSubTrigger as DropdownMenuSubTriggerV1,
} from "@repo/design-system/components/ui/dropdown-menu";
import {
  SelectContent as SelectContentV1,
  SelectItem as SelectItemV1,
  SelectSeparator as SelectSeparatorV1,
  SelectTrigger as SelectTriggerV1,
} from "@repo/design-system/components/ui/select";
import { cn } from "@repo/design-system/lib/utils";
import type { ComponentProps } from "react";

/**
 * v2 Dropdown — restyles v1's `DropdownMenu` (`components/ui/dropdown-menu.tsx`,
 * Radix-backed) and `Select` (`components/ui/select.tsx`, a hand-built listbox)
 * to match the Figma "Remote Leverage — Design System" file, Dropdown component
 * node `358:45`. All Radix interaction/accessibility (DropdownMenu: roving
 * focus, keyboard nav, portal/positioning) and all of Select's own state
 * machine (open/close, controlled/uncontrolled value, label registry,
 * viewport-portal positioning) stay exactly as v1 implements them — this file
 * only overrides the visual layer.
 *
 * Scope call — why both v1 files are wrapped in one component, from what the
 * Figma node actually contains (not guessed): node `358:45` only shows two
 * sub-components — "Dropdown Item" (163:1843 family: an icon+label row across
 * four states, Default/Hover/Focus/Active-with-trailing-checkmark) and
 * "Dropdown Trigger" (358:87: the same rows stacked inside a rounded, shadowed
 * panel). There is no closed "value + chevron" control drawn anywhere in this
 * node — "Trigger" here names the open panel, not a closed form control.
 * That single row+panel design reads as generic "pick one item from a list"
 * chrome, which is exactly what both v1 primitives render for their
 * content/item parts, so both get the same token-driven restyle:
 * - `Select`'s `SelectItem` is the closer structural match: it already has a
 *   `selected` concept with a trailing right-aligned checkmark (`absolute
 *   right-2`), the same position Figma's "Active" checkmark uses. It also
 *   uses independent `hover:`/`focus:` Tailwind classes on a plain (non-
 *   focusable) `<div>`, which is why Figma's Hover (gray) and Focus (purple)
 *   can be restyled as two genuinely different colors here.
 * - `DropdownMenu`'s `DropdownMenuItem`/`SubTrigger` get the same row sizing
 *   and panel chrome for the action-menu use case. Radix's own roving-focus
 *   model makes pointer-hover and keyboard-focus the same underlying
 *   `focus:`-matched state (Radix moves real DOM focus on pointer-enter), so
 *   there is no way to show two different colors for them here — both use
 *   Figma's Focus (purple) treatment; this is a Radix behavior difference
 *   from Select's plain-div items, not a restyle choice.
 * - Plain `DropdownMenuItem` has no built-in "selected" concept at all (it's
 *   an action, not a list choice) — reusing v1's own `DropdownMenuCheckboxItem`
 *   /`DropdownMenuRadioItem` for the action-menu "selected" case (rather than
 *   inventing a new selected/checkmark prop on `DropdownMenuItem`) keeps this
 *   file's only new interaction concept where v1 already defined one.
 *
 * Deliberately out of scope:
 * - `SelectContent`'s outer floating panel (`rounded-md border bg-popover
 *   shadow-lg`, applied by v1's `ViewportPortalLayer`) has no prop hook that
 *   reaches it — `SelectContent`'s own `className` only reaches the *inner*
 *   scroll container, not the bordered/shadowed outer `<div
 *   data-portal-dropdown>`. Overriding it would mean editing a v1 file, which
 *   is forbidden. `SelectItem`'s row colors/divider are restyled below
 *   (reachable and Figma-verified); the outer panel radius/shadow for Select
 *   specifically stays v1's default as a known, documented gap.
 *   `DropdownMenuContent` has no such split (Radix renders one real content
 *   element), so its panel is restyled to full Figma fidelity below.
 * - `SelectTrigger` (the closed control) and `SelectValue` have no Figma node
 *   in this file at all. `SelectTrigger` gets a light, interpolated restyle
 *   (row height/radius/border consistent with the item rows below it, same
 *   pattern as v2 Button's icon-sm/icon-lg sizes) so the component still has
 *   a usable closed state; `SelectValue` is left as v1's verbatim export —
 *   it conditionally colors the placeholder vs. filled value internally
 *   (`!show && "text-muted-foreground"`), and this file has no Figma-backed
 *   reason to risk breaking that logic from the outside.
 * - `DropdownMenuLabel`/`SelectLabel` (section headings) have no Figma node
 *   here either — left as verbatim v1 exports.
 * - `DropdownMenuCheckboxItem`/`RadioItem`'s indicator icon color is left as
 *   v1's default (`currentColor`, inherited from row text) — recoloring only
 *   the check icon independently of the row's label text isn't reachable
 *   without editing v1's hardcoded `ItemIndicator` markup, the same
 *   constraint noted for Select's outer panel above.
 */

const rowStateOverrideClassName = cn(
  "focus:bg-(--v2-dropdown-bg-active) focus:text-(--v2-dropdown-text)",
  "data-[state=open]:bg-(--v2-dropdown-bg-active) data-[state=open]:text-(--v2-dropdown-text)"
);

/* ─── DropdownMenu (action-menu style, wraps dropdown-menu.tsx) ─────────── */

function DropdownMenuContent({
  className,
  ...props
}: ComponentProps<typeof DropdownMenuContentV1>) {
  return (
    <DropdownMenuContentV1
      className={cn(
        "ds-v2 rounded-[12px] border-none bg-(--v2-dropdown-bg) p-1 shadow-(--v2-dropdown-shadow)",
        className
      )}
      {...props}
    />
  );
}

function DropdownMenuSubContent({
  className,
  ...props
}: ComponentProps<typeof DropdownMenuSubContentV1>) {
  return (
    <DropdownMenuSubContentV1
      className={cn(
        "ds-v2 rounded-[12px] border-none bg-(--v2-dropdown-bg) p-1 shadow-(--v2-dropdown-shadow)",
        className
      )}
      {...props}
    />
  );
}

function DropdownMenuItem({
  className,
  ...props
}: ComponentProps<typeof DropdownMenuItemV1>) {
  return (
    <DropdownMenuItemV1
      className={cn(
        "h-10 rounded-none border-(--v2-dropdown-border) border-b px-3 last:border-b-0",
        rowStateOverrideClassName,
        className
      )}
      {...props}
    />
  );
}

function DropdownMenuSubTrigger({
  className,
  ...props
}: ComponentProps<typeof DropdownMenuSubTriggerV1>) {
  return (
    <DropdownMenuSubTriggerV1
      className={cn(
        "h-10 rounded-none border-(--v2-dropdown-border) border-b px-3 last:border-b-0",
        rowStateOverrideClassName,
        className
      )}
      {...props}
    />
  );
}

function DropdownMenuCheckboxItem({
  className,
  ...props
}: ComponentProps<typeof DropdownMenuCheckboxItemV1>) {
  return (
    <DropdownMenuCheckboxItemV1
      className={cn(
        "h-10 border-(--v2-dropdown-border) border-b last:border-b-0",
        rowStateOverrideClassName,
        className
      )}
      {...props}
    />
  );
}

function DropdownMenuRadioItem({
  className,
  ...props
}: ComponentProps<typeof DropdownMenuRadioItemV1>) {
  return (
    <DropdownMenuRadioItemV1
      className={cn(
        "h-10 border-(--v2-dropdown-border) border-b last:border-b-0",
        rowStateOverrideClassName,
        className
      )}
      {...props}
    />
  );
}

function DropdownMenuSeparator({
  className,
  ...props
}: ComponentProps<typeof DropdownMenuSeparatorV1>) {
  return (
    <DropdownMenuSeparatorV1
      className={cn("bg-(--v2-dropdown-border)", className)}
      {...props}
    />
  );
}

// biome-ignore lint/performance/noBarrelFile: re-exporting v1's DropdownMenu sub-parts that need no visual changes verbatim
export {
  DropdownMenu,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuTrigger,
} from "@repo/design-system/components/ui/dropdown-menu";

export {
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
};

/* ─── Select (form control style, wraps select.tsx) ─────────────────────── */

function SelectContent({
  className,
  ...props
}: ComponentProps<typeof SelectContentV1>) {
  return (
    <SelectContentV1
      className={cn("ds-v2 rounded-[12px] bg-(--v2-dropdown-bg)", className)}
      {...props}
    />
  );
}

function SelectItem({
  className,
  ...props
}: ComponentProps<typeof SelectItemV1>) {
  return (
    <SelectItemV1
      className={cn(
        "h-10 items-center rounded-none border-(--v2-dropdown-border) border-b py-0 last:border-b-0",
        "text-(--v2-dropdown-text) hover:bg-(--v2-dropdown-bg-hover) hover:text-(--v2-dropdown-text)",
        "focus:bg-(--v2-dropdown-bg-active) focus:text-(--v2-dropdown-text)",
        "aria-selected:bg-(--v2-dropdown-bg-active)",
        "[&>span:first-child_svg]:text-(--v2-dropdown-check)",
        className
      )}
      {...props}
    />
  );
}

function SelectSeparator({
  className,
  ...props
}: ComponentProps<typeof SelectSeparatorV1>) {
  return (
    <SelectSeparatorV1
      className={cn("bg-(--v2-dropdown-border)", className)}
      {...props}
    />
  );
}

/**
 * No Figma node backs this closed control (see file header) — restyled just
 * enough to feel like the same family as the item rows above it: matching
 * radius/border/height, hover feedback, and a chevron that rotates with
 * `aria-expanded` (v1 already renders the chevron itself; only its rotation
 * is added here).
 */
function SelectTrigger({
  className,
  ...props
}: ComponentProps<typeof SelectTriggerV1>) {
  return (
    <SelectTriggerV1
      className={cn(
        "ds-v2 w-full justify-between rounded-[10px] border-(--v2-dropdown-border) bg-(--v2-dropdown-bg) px-3 text-(--v2-dropdown-text) shadow-none data-[size=default]:h-10",
        "hover:bg-(--v2-dropdown-bg-hover) dark:bg-(--v2-dropdown-bg) dark:hover:bg-(--v2-dropdown-bg-hover)",
        "focus-visible:border-(--v2-dropdown-check) focus-visible:ring-(--v2-dropdown-check)/20",
        "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-(--v2-dropdown-bg)",
        "[&>svg]:transition-transform aria-expanded:[&>svg]:rotate-180",
        className
      )}
      {...props}
    />
  );
}

export {
  Select,
  SelectGroup,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectValue,
} from "@repo/design-system/components/ui/select";

export { SelectContent, SelectItem, SelectSeparator, SelectTrigger };
